import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileText, Image as ImageIcon, Eye, Upload, Sparkles, Pencil } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { AppShell } from "@/components/AppShell";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DocumentPreview } from "@/components/DocumentPreview";
import { DocDeleteButton } from "@/components/DocDeleteButton";

function fmtSize(bytes) {
  if (!bytes) return "";
  const kb = bytes / 1024;
  return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`;
}

export default function Documents() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [preview, setPreview] = useState(null);
  const [renameDoc, setRenameDoc] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [savingRename, setSavingRename] = useState(false);

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["documents"],
    queryFn: async () => (await api.get("/documents")).data,
  });

  const openRename = (d) => {
    setRenameDoc(d);
    setRenameValue(d.original_filename || "");
  };

  const saveRename = async () => {
    const name = renameValue.trim();
    if (!name || name === renameDoc.original_filename) {
      setRenameDoc(null);
      return;
    }
    setSavingRename(true);
    try {
      await api.patch(`/documents/${renameDoc.id}`, { original_filename: name });
      toast.success(t("doc.renamed"));
      qc.invalidateQueries({ queryKey: ["documents"] });
      setRenameDoc(null);
    } catch (err) {
      toast.error(apiError(err, t));
    } finally {
      setSavingRename(false);
    }
  };

  return (
    <AppShell title={t("nav.documents")}>
      <div className="esv-rise mb-5 flex items-center justify-between">
        <h2 className="font-display text-2xl font-extrabold text-[#1A2E26]">{t("doc.myDocuments")}</h2>
        <button
          data-testid="documents-upload-button"
          onClick={() => navigate("/documents/new")}
          className="flex items-center gap-1.5 rounded-full bg-[#2D5A4C] px-3.5 py-2 text-sm font-semibold text-white active:scale-95"
        >
          <Upload size={16} /> {t("nav.newDocument")}
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-2xl bg-[#F0F4F1]" />)}
        </div>
      ) : docs.length === 0 ? (
        <p className="rounded-xl border border-[#E2EAE5] bg-white/60 px-3 py-6 text-center text-sm text-[#B0C2B8]">
          {t("doc.noDocuments")}
        </p>
      ) : (
        <div className="space-y-2">
          {docs.map((d) => {
            const isPdf = d.content_type === "application/pdf";
            return (
              <div
                key={d.id}
                data-testid={`documents-item-${d.id}`}
                className="flex w-full items-center gap-2 rounded-2xl border border-[#E2EAE5] bg-white p-3 transition-all hover:border-[#C4DFD2]"
              >
                <button
                  type="button"
                  data-testid={`documents-open-${d.id}`}
                  onClick={() => setPreview(d)}
                  className="flex min-w-0 flex-1 items-center gap-3 text-left active:scale-[0.99]"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F0F4F1] text-[#2D5A4C]">
                    {isPdf ? <FileText size={18} /> : <ImageIcon size={18} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-[#1A2E26]">{d.original_filename}</p>
                    <p className="text-[11px] text-[#7A9488]">{fmtSize(d.size)}</p>
                  </div>
                  <Eye size={17} className="shrink-0 text-[#7A9488]" />
                </button>
                <button
                  type="button"
                  data-testid={`documents-rename-${d.id}`}
                  onClick={() => openRename(d)}
                  className="rounded-full p-2 text-[#4A6358] transition-colors hover:bg-[#F0F4F1] active:scale-95"
                  aria-label={t("doc.rename")}
                >
                  <Pencil size={16} />
                </button>
                <button
                  type="button"
                  data-testid={`documents-analyze-${d.id}`}
                  onClick={() => navigate(`/documents/${d.id}/analysis`)}
                  className="rounded-full p-2 text-[#2D5A4C] transition-colors hover:bg-[#F0F4F1] active:scale-95"
                  aria-label="analyze"
                >
                  <Sparkles size={17} />
                </button>
                <DocDeleteButton doc={d} />
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-md p-0">
          {preview && <DocumentPreview doc={preview} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!renameDoc} onOpenChange={(o) => !o && setRenameDoc(null)}>
        <DialogContent data-testid="document-rename-dialog" className="max-w-[340px] rounded-2xl">
          <DialogHeader>
            <DialogTitle>{t("doc.renameTitle")}</DialogTitle>
          </DialogHeader>
          <Input
            data-testid="document-rename-input"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && saveRename()}
            autoFocus
            dir="auto"
            className="h-12 rounded-xl"
          />
          <DialogFooter>
            <button
              data-testid="document-rename-cancel"
              onClick={() => setRenameDoc(null)}
              className="rounded-xl border border-[#E2EAE5] px-4 py-2.5 text-sm font-semibold text-[#4A6358] active:scale-[0.98]"
            >
              {t("common.cancel")}
            </button>
            <button
              data-testid="document-rename-save"
              onClick={saveRename}
              disabled={savingRename}
              className="rounded-xl bg-[#2D5A4C] px-4 py-2.5 text-sm font-semibold text-white active:scale-[0.98] disabled:opacity-50"
            >
              {savingRename ? t("common.saving") : t("common.save")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
